/* SKIN QUIZ engine — pure recommendation logic. No DOM, no network.
 *
 * Loaded two ways:
 * - Browser: <script src="skin-quiz-engine.js"> sets window.SkinQuizEngine.
 * - Node tests: require() / createRequire() gets the same object
 *   (channels/shopify/skin-quiz-2026-09/test/engine.test.mjs).
 *
 * Config comes from snippets/skin-quiz-config.liquid (fixture:
 * test/fixtures/config.json). Money is integer cents throughout, Liquid's
 * native unit; the controller formats it.
 *
 * Safety rules live HERE, not in config, so nobody can edit them away:
 * - reactivity = often       -> drop retinoid, acid, physical_exfoliant
 * - pregnant = yes / unsure  -> drop retinoid (not asked, and ignored, at 45+)
 * - unavailable / missing    -> drop
 * Products carry the flags; this file decides what the flags mean.
 *
 * Slot resolution: rows are evaluated top to bottom. The first matching row
 * offers [product, ...alternates]; the first survivor that isn't already
 * used by an earlier slot wins. If nothing survives, try the next matching
 * row. If still nothing, the slot is hidden (never an empty card).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.SkinQuizEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SLOT_ORDER = ['cleanse', 'treat_am', 'treat_pm', 'moisturize', 'spf'];
  var NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'];
  // Answer keys that must never be stored, shared, or sent anywhere.
  var PRIVATE_KEYS = ['pregnant'];
  // Age bands that never see the pregnancy question.
  var NO_PREGNANCY_AGES = ['45_54', '55_64', '65p'];

  function has(list, value) {
    return !!list && list.indexOf(value) !== -1;
  }

  // ---------- safety ----------

  function blockedFlags(answers, opts) {
    var flags = [];
    if (answers.reactivity === 'often') flags.push('retinoid', 'acid', 'physical_exfoliant');
    if (!(opts && opts.ignorePregnancy) && !has(NO_PREGNANCY_AGES, answers.age_band) &&
        (answers.pregnant === 'yes' || answers.pregnant === 'unsure')) flags.push('retinoid');
    return flags;
  }

  function isAllowed(handle, config, blocked) {
    var p = config.products && config.products[handle];
    if (!p || p.available === false) return false;
    var flags = p.flags || [];
    for (var i = 0; i < flags.length; i++) {
      if (has(blocked, flags[i])) return false;
    }
    return true;
  }

  function hasFlag(handle, config, flag) {
    var p = config.products && config.products[handle];
    return !!p && has(p.flags, flag);
  }

  // ---------- rule matching ----------

  // when: { answer_key: [values] }                   -> answer is one of values
  //       { answer_key: { not: [values] } }          -> answer is none of values
  //       { 'slot.treat_pm': [handles] | {not: []} } -> resolved slot handle
  // Empty / missing when = always.
  function matches(when, answers, chosen) {
    if (!when) return true;
    for (var key in when) {
      if (!Object.prototype.hasOwnProperty.call(when, key)) continue;
      var actual = key.indexOf('slot.') === 0 ? chosen[key.slice(5)] || null : answers[key];
      var cond = when[key];
      if (Array.isArray(cond)) {
        if (!has(cond, actual)) return false;
      } else if (cond && Array.isArray(cond.not)) {
        if (has(cond.not, actual)) return false;
      } else {
        return false; // unknown condition shape: fail closed
      }
    }
    return true;
  }

  function resolveSlot(slotDef, answers, config, blocked, used, chosen) {
    var rules = slotDef.rules || [];
    for (var r = 0; r < rules.length; r++) {
      var row = rules[r];
      if (!matches(row.when, answers, chosen)) continue;
      var candidates = [row.product].concat(row.alternates || []);
      var survivors = [];
      for (var c = 0; c < candidates.length; c++) {
        var h = candidates[c];
        if (isAllowed(h, config, blocked) && !used[h] && !has(survivors, h)) survivors.push(h);
      }
      if (survivors.length) return { handle: survivors[0], alternates: survivors.slice(1) };
    }
    return null;
  }

  function resolveSlots(answers, config, blocked) {
    var used = {};
    var chosen = {};
    var out = [];
    var defs = orderedSlotDefs(config);
    for (var i = 0; i < defs.length; i++) {
      var res = resolveSlot(defs[i], answers, config, blocked, used, chosen);
      if (!res) continue;
      used[res.handle] = true;
      chosen[defs[i].slot] = res.handle;
      out.push({ def: defs[i], handle: res.handle, alternates: res.alternates });
    }
    // An alternate can't be something another slot is already showing,
    // otherwise a swap would create a duplicate.
    for (var j = 0; j < out.length; j++) {
      out[j].alternates = out[j].alternates.filter(function (h) { return !used[h]; });
    }
    return out;
  }

  function orderedSlotDefs(config) {
    var byKey = {};
    (config.slots || []).forEach(function (s) { byKey[s.slot] = s; });
    return SLOT_ORDER.filter(function (k) { return byKey[k]; }).map(function (k) { return byKey[k]; });
  }

  // ---------- swaps ----------

  // swaps: { slot: handle }. Only honored when handle is one of that slot's
  // (already safety-filtered) alternates, so a hand-edited share URL can't
  // put a blocked product on the page.
  function applySwaps(resolved, swaps) {
    if (!swaps) return;
    resolved.forEach(function (r) {
      var want = swaps[r.def.slot];
      if (!want || want === r.handle || !has(r.alternates, want)) return;
      var rest = r.alternates.filter(function (h) { return h !== want; });
      r.alternates = [r.handle].concat(rest);
      r.defaultHandle = r.handle;
      r.handle = want;
    });
  }

  // ---------- money ----------

  function productPrice(handle, config) {
    var p = config.products[handle];
    return Math.round(Number(p.price) || 0);
  }

  // Mirrors the bundle-discount function's tier pick: best enabled tier the
  // unit count reaches, and 20%+ never below 4 units.
  function pickTier(units, config) {
    if (config.show_tier_pricing === false) return null;
    var cfg = config.cart_tiers;
    if (!cfg || cfg.enabled === false || !Array.isArray(cfg.tiers)) return null;
    var percent = 0;
    cfg.tiers.forEach(function (t) {
      if (!t || t.enabled === false) return;
      var min = parseInt(t.min_units, 10);
      var pct = parseInt(t.percent, 10);
      if (!(min > 0) || !(pct > 0)) return;
      if (pct >= 20 && min < 4) return;
      if (units >= min && pct > percent) percent = pct;
    });
    return percent > 0 ? percent : null;
  }

  function totals(handles, config) {
    var price = 0;
    var tierPrice = 0;
    var percent = pickTier(handles.length, config);
    handles.forEach(function (h) {
      var p = config.products[h];
      var cents = productPrice(h, config);
      var compareAt = Math.round(Number(p.compare_at_price) || 0);
      price += cents;
      // Sale items count toward the tier but don't receive it (function rule).
      tierPrice += (percent && !(compareAt > cents)) ? Math.round(cents * (100 - percent) / 100) : cents;
    });
    return {
      total: { price: price, count: handles.length },
      tier: percent ? { rate: percent / 100, percent: percent, price: tierPrice, label: (config.cart_tiers.label || '') } : null
    };
  }

  // ---------- copy ----------

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m;
    });
  }

  // [3] -> "step 3", [1, 4] -> "steps 1 and 4". Step numbers are positions
  // on the page, so they shift when a slot is hidden.
  function joinSteps(nums) {
    if (nums.length === 1) return 'step ' + nums[0];
    return 'steps ' + nums.slice(0, -1).join(', ') + ' and ' + nums[nums.length - 1];
  }

  function summary(answers, config, count) {
    var ph = config.phrases || {};
    var override = (ph.summary_by_concern || {})[answers.concern_1];
    var c2 = answers.concern_2;
    var hasC2 = c2 && c2 !== 'none' && c2 !== answers.concern_1 && ph.concern_short && ph.concern_short[c2];
    return fill(override || ph.summary, {
      skin: (ph.skin || {})[answers.skin_type] || '',
      concern: (ph.concern || {})[answers.concern_1] || '',
      c2: hasC2 ? fill(ph.c2, { c2: ph.concern_short[c2] }) : '',
      count: NUMBER_WORDS[count] || String(count)
    });
  }

  function whyFor(slot, handle, config) {
    return ((config.why || {})[slot] || {})[handle] || '';
  }

  // ---------- main ----------

  function recommend(answers, config, swaps, removed) {
    // Ignore answers to questions this path skips. A saved or shared payload
    // can carry a stale concern_2 after someone switches Q1 to "Nothing in
    // particular"; it must not come back as an add-on.
    answers = effectiveAnswers(answers, config);
    var routineDef = (config.routines || {})[answers.concern_1] || null;

    // Would the page show a retinoid if we didn't ask? That's when we ask.
    var unasked = resolveSlots(answers, config, blockedFlags(answers, { ignorePregnancy: true }));
    var unaskedPm = find(unasked, 'treat_pm');
    var needsSafetyQuestion = !!unaskedPm && hasFlag(unaskedPm.handle, config, 'retinoid') &&
      !has(NO_PREGNANCY_AGES, answers.age_band);

    var blocked = blockedFlags(answers);
    var resolved = resolveSlots(answers, config, blocked);
    var safetySwap = needsSafetyQuestion && (answers.pregnant === 'yes' || answers.pregnant === 'unsure');

    // What treat_pm becomes if they answer yes (for the "We'll use X instead" hint).
    var safetyAlternate = null;
    if (needsSafetyQuestion) {
      var yes = resolveSlots(answers, config, blocked.concat('retinoid'));
      var yesPm = find(yes, 'treat_pm');
      safetyAlternate = yesPm ? yesPm.handle : null;
    }

    applySwaps(resolved, swaps);
    if (removed && removed.length) {
      resolved = resolved.filter(function (r) { return !has(removed, r.def.slot); });
    }

    var startDef = (config.start_here || {})[answers.routine_now] || { slots: [], note: '' };
    var startSteps = [];
    var slots = resolved.map(function (r, i) {
      var startHere = has(startDef.slots, r.def.slot);
      if (startHere) startSteps.push(i + 1);
      return {
        slot: r.def.slot,
        label: r.def.label,
        noun: r.def.noun || r.def.label,
        when: r.def.when,
        handle: r.handle,
        defaultHandle: r.defaultHandle || r.handle,
        alternates: r.alternates,
        why: whyFor(r.def.slot, r.handle, config),
        startHere: startHere
      };
    });

    var usedHandles = slots.map(function (s) { return s.handle; });
    // Every matching row adds its product, up to max_add_ons (default 1).
    var addOns = [];
    var maxAddOns = config.max_add_ons || 1;
    var addOnRules = config.add_on || [];
    var chosen = {};
    slots.forEach(function (s) { chosen[s.slot] = s.handle; });
    for (var a = 0; a < addOnRules.length && addOns.length < maxAddOns; a++) {
      var row = addOnRules[a];
      if (!matches(row.when, answers, chosen)) continue;
      if (!isAllowed(row.product, config, blocked) || has(usedHandles, row.product)) continue;
      if (addOns.some(function (x) { return x.handle === row.product; })) continue;
      addOns.push({ handle: row.product, why: whyFor('add_on', row.product, config) });
    }

    var money = totals(usedHandles, config);

    return {
      routine: routineDef ? {
        key: routineDef.key,
        name: routineDef.name,
        summary: summary(answers, config, slots.length),
        accentSku: routineDef.accent_sku,
        accentBg: routineDef.accent_bg || null,
        accentHex: routineDef.accent_hex || null
      } : null,
      slots: slots,
      addOn: addOns[0] || null,
      addOns: addOns,
      startHereNote: startSteps.length ? fill(startDef.note, { steps: joinSteps(startSteps) }) : '',
      needsSafetyQuestion: needsSafetyQuestion,
      safetyAlternate: safetyAlternate,
      safetySwap: safetySwap,
      rulesStatus: config.rules_status || null,
      total: money.total,
      tier: money.tier
    };
  }

  function find(resolved, slot) {
    for (var i = 0; i < resolved.length; i++) if (resolved[i].def.slot === slot) return resolved[i];
    return null;
  }

  // ---------- flow helpers ----------

  // Ordered question list for these answers: Q1–Q6, plus the safety question
  // when it's needed. Q2's answers exclude the Q1 pick.
  // Questions that an already-chosen answer says to skip. Q1's "Nothing in
  // particular" skips Q2, because there's no second concern to ask about.
  function skippedKeys(answers, config) {
    var skipped = [];
    (config.questions || []).forEach(function (q) {
      var value = answers[q.key];
      if (!value) return;
      (q.answers || []).forEach(function (a) {
        if (a.value === value && Array.isArray(a.skips)) skipped = skipped.concat(a.skips);
      });
    });
    return skipped;
  }

  function effectiveAnswers(answers, config) {
    answers = answers || {};
    var skipped = skippedKeys(answers, config);
    if (!skipped.length) return answers;
    var out = {};
    for (var k in answers) if (!has(skipped, k)) out[k] = answers[k];
    return out;
  }

  function questionFlow(answers, config) {
    answers = answers || {};
    var skipped = skippedKeys(answers, config);
    var out = [];
    (config.questions || []).forEach(function (q) {
      if (has(skipped, q.key)) return;
      if (q.type === 'conditional') {
        if (q.key === 'pregnant' && recommend(answers, config).needsSafetyQuestion) out.push(q);
        return;
      }
      if (q.key === 'concern_2') {
        out.push(Object.assign({}, q, {
          answers: q.answers.filter(function (a) { return a.value !== answers.concern_1; })
        }));
        return;
      }
      out.push(q);
    });
    return out;
  }

  function isComplete(answers, config) {
    var flow = questionFlow(answers, config);
    for (var i = 0; i < flow.length; i++) {
      var v = answers[flow[i].key];
      if (v === undefined || v === null || v === '') return false;
      if (!flow[i].answers.some(function (a) { return a.value === v; })) return false;
    }
    return true;
  }

  // Copy of an answers object with the private keys removed. Use before
  // anything leaves the page: localStorage, share URL, analytics, Klaviyo.
  function stripPrivate(answers) {
    var out = {};
    for (var k in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, k) && !has(PRIVATE_KEYS, k)) out[k] = answers[k];
    }
    return out;
  }

  return {
    SLOT_ORDER: SLOT_ORDER,
    PRIVATE_KEYS: PRIVATE_KEYS,
    recommend: recommend,
    questionFlow: questionFlow,
    skippedKeys: skippedKeys,
    effectiveAnswers: effectiveAnswers,
    isComplete: isComplete,
    stripPrivate: stripPrivate,
    pickTier: pickTier
  };
});
